'use client';
import React, { useEffect, useRef } from 'react';
import { UserCheck, UserX } from 'lucide-react';
import { STAFF_MEMBERS } from './bookingMockData';

interface StaffAssignDropdownProps {
  currentStaff: string | null;
  onSelect: (s: string) => void;
  onClose: () => void;
}

export default function StaffAssignDropdown({ currentStaff, onSelect, onClose }: StaffAssignDropdownProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const initials = (name: string) =>
    name.split(' ').map((n) => n[0]).join('').toUpperCase();

  const avatarColors = ['bg-primary', 'bg-purple-500', 'bg-emerald-500'];

  return (
    <div
      ref={ref}
      className="absolute left-0 top-full mt-1 z-50 bg-card border border-border rounded-xl shadow-modal py-1.5 min-w-[180px] animate-fade-in"
      role="listbox"
      aria-label="Assign staff member"
    >
      <p className="text-[10px] font-600 text-muted-foreground uppercase tracking-wider px-3 py-1.5">Assign Staff</p>
      {STAFF_MEMBERS.map((name, idx) => (
        <button
          key={`staff-opt-${name}`}
          onClick={() => onSelect(name)}
          className={`w-full flex items-center gap-2.5 px-3 py-2 hover:bg-muted transition-colors text-left ${
            currentStaff === name ? 'bg-secondary' : ''
          }`}
          role="option"
          aria-selected={currentStaff === name}
        >
          <div className={`w-6 h-6 rounded-full ${avatarColors[idx % avatarColors.length]} flex items-center justify-center text-white text-[10px] font-700 flex-shrink-0`}>
            {initials(name)}
          </div>
          <span className="text-sm font-500 text-foreground">{name}</span>
          {currentStaff === name && (
            <span className="ml-auto text-primary">
              <UserCheck size={13} />
            </span>
          )}
        </button>
      ))}
      {currentStaff && (
        <>
          <hr className="border-border my-1" />
          <button
            onClick={() => onSelect('')}
            className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-red-50 transition-colors text-left text-red-500"
          >
            <UserX size={13} />
            <span className="text-xs font-500">Unassign</span>
          </button>
        </>
      )}
    </div>
  );
}